<template>
  <div
    id="selectormapInternal"
    class="map" />
</template>
<script lang="ts">
  import { DateTime } from 'luxon';
  import mapboxgl from 'mapbox-gl';

  import Colors from '@/Colors';
  import Geofence from '@/common/models/geo/Geofence';
  import { getContainerHeight, getContainerWidth } from '@/globals';
  import MapStyleControl from '@/org-app/components/geofenceGroups/MapStyleControl';
  import UnitClustersService from '@/org-app/components/geofenceGroups/UnitClustersService';

  export default {
    props: {
      geofences: {
        type: Array<Geofence>,
        default: () => [],
      },
      requestedForUtc: {
        type: DateTime,
      },
    },
    emits: ['onActiveUnitsChange', 'unitClustersChanged'],
    data() {
      return {
        map: null,
        clusterRadius: 40,
        mapNavigationControl: null,
        mapFullscreenControl: null,
        mapStyle: 'mapbox://styles/mapbox/light-v9',
        mapStyleControl: null,
        unitClusters: null,
        areGeofenceBoundingBoxesVisible: false,
        activeGeofence: null,
        unitClustersService: null,
        activeUnits: [],
      };
    },
    async mounted() {
      this.initMap();
    },
    unmounted() {
      this.unitClustersService?.$destroy();
    },
    methods: {
      initMap() {
        const mapOptions = {
          container: 'selectormapInternal',
          style: this.mapStyle,
          minzoom: 1.3,
          zoom: 1,
        };
        const map = new mapboxgl.Map(mapOptions);
        this.map = map;
        this.unitClustersService = new UnitClustersService(this.map, this.requestedForUtc);

        map.on('load', () => {
          this.createMapLayers();

          this.map.on('click', 'place_points_layer', (e) => {
            const { id } = e.features[0].properties;
            this.activateGeofence(id);
          });

          this.unitClustersService.init(true, true, 'place_points_layer');

          this.unitClustersService.activeUnits$.subscribe((units) => {
            this.activeUnits = units;
            this.$emit('onActiveUnitsChange', this.activeUnits);
          });

          this.unitClustersService.activeUnitClusters$.subscribe((unitClusters) => {
            this.$emit('unitClustersChanged', unitClusters);
          });

          this.mapNavigationControl = new mapboxgl.NavigationControl();
          this.mapFullscreenControl = new mapboxgl.FullscreenControl();
          this.mapStyleControl = new MapStyleControl();
          this.mapStyleControl.clickHandler = (isSatellite) => {
            if (isSatellite) {
              this.changeMapStyleToSatellite();
            } else {
              this.changeMapStyleToLight();
            }
          };
          this.showMapControls(this.map);
          this.map.boxZoom.enable();

          this.updateGeofenceDataSources();
          this.fitAllGeofencesIntoView();
        });

        return map;
      },
      showMapControls(map) {
        const mapContainer = window.document.getElementById('selectormapInternal');
        if (mapContainer == null) {
          return;
        }
        const controlContainer = mapContainer.getElementsByClassName('mapboxgl-control-container')[0];
        if (controlContainer != null) {
          controlContainer.setAttribute('style', 'opacity: 1;');
        }
        map.addControl(this.mapStyleControl);
        map.addControl(this.mapNavigationControl);
      },
      createMapLayers() {
        if (this.mapStyle.indexOf('satellite') === -1) {
          if (this.map.getLayer('3d-buildings') == null) {
            this.map.addLayer({
              id: '3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 15,
              paint: {
                'fill-extrusion-color': '#aaa',
                'fill-extrusion-height': {
                  type: 'identity',
                  property: 'height',
                },
                'fill-extrusion-base': {
                  type: 'identity',
                  property: 'this.sites',
                },
                'fill-extrusion-opacity': 0.6,
              },
            });
          }
        }

        const geofenceLayerData = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        };

        if (this.map.getSource('place_polygons_source') != null) {
          this.map.removeLayer('place_polygons_fill_layer');
          this.map.removeLayer('place_polygons_line_layer');
          this.map.removeSource('place_polygons_source');
        }

        this.map.addSource('place_polygons_source', geofenceLayerData);

        const geofenceCentroidLayerData = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        };

        if (this.map.getSource('place_points_source') != null) {
          this.map.removeLayer('place_points_layer');
          this.map.removeSource('place_points_source');
        }
        this.map.addSource('place_points_source', geofenceCentroidLayerData);

        const geofencBoundingBoxLayerData = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        };

        if (this.areGeofenceBoundingBoxesVisible) {
          if (this.map.getSource('place_bbox_source') != null) {
            this.map.removeLayer('place_polygons_bbox_layer');
            this.map.removeSource('place_bbox_source');
          }
          this.map.addSource('place_bbox_source', geofencBoundingBoxLayerData);

          this.map.addLayer({
            id: 'place_polygons_bbox_layer',
            type: 'line',
            source: 'place_bbox_source',
            paint: {
              'line-color': Colors.secondary,
            },
          });
        }

        this.map.addLayer({
          id: 'place_polygons_fill_layer',
          type: 'fill-extrusion',
          source: 'place_polygons_source',
          paint: {
            'fill-extrusion-opacity': 0.3,
            'fill-extrusion-height': {
              property: 'extrusion',
              type: 'identity',
            },
            'fill-extrusion-base': 0,
            'fill-extrusion-color': [
              'match',
              ['get', 'isSelected'],
              'true',
              Colors.geofences,
              'false',
              '#999999',
              Colors.geofences,
            ],
          },
        });

        this.map.addLayer({
          id: 'place_polygons_line_layer',
          type: 'line',
          source: 'place_polygons_source',
          paint: {
            'line-color': [
              'match',
              ['get', 'isSelected'],
              'true',
              Colors.geofences,
              'false',
              '#999999',
              Colors.geofences,
            ],
          },
        });

        this.map.addLayer({
          id: 'place_points_layer',
          type: 'circle',
          source: 'place_points_source',
          paint: {
            'circle-color': [
              'match',
              ['get', 'isSelected'],
              'true',
              Colors.geofences,
              'false',
              '#999999',
              Colors.geofences,
            ],
            'circle-radius': 4,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });
      },
      changeMapStyleToSatellite() {
        this.mapStyle = 'mapbox://styles/mapbox/satellite-streets-v9';
        this.map.setStyle(this.mapStyle);
        setTimeout(() => {
          this.createMapLayers();
          this.updateGeofenceDataSources();
        }, 1000);
      },
      changeMapStyleToLight() {
        this.mapStyle = 'mapbox://styles/mapbox/light-v9';
        this.map.setStyle(this.mapStyle);
        setTimeout(() => {
          this.createMapLayers();
          this.updateGeofenceDataSources();
        }, 1000);
      },
      updateGeofenceDataSources() {
        this.updateGeofencePolygonDataSource();
        this.updateGeofencePointDataSource();
      },
      updateGeofencePolygonDataSource() {
        const geofencePolygonsLayerData = this.map.getSource('place_polygons_source');
        if (geofencePolygonsLayerData === undefined) {
          return;
        }
        const features = this.geofences.map((x) => x.feature);
        geofencePolygonsLayerData.setData({
          type: 'FeatureCollection',
          features,
        });
      },
      updateGeofencePointDataSource() {
        const geofenceCentroidLayerData = this.map.getSource('place_points_source');
        if (geofenceCentroidLayerData === undefined) {
          return;
        }
        const pointFeatures = this.geofences.map((x) => x.pointFeature);
        geofenceCentroidLayerData.setData({
          type: 'FeatureCollection',
          features: pointFeatures,
        });
      },
      fitAllGeofencesIntoView() {
        if (this.geofences.length === 0) {
          return;
        }
        const minLat = Math.min(...this.geofences.map((x) => x.boundsSwLat)) || 0;
        const maxLat = Math.max(...this.geofences.map((x) => x.boundsNeLat)) || 0;
        const minLng = Math.min(...this.geofences.map((x) => x.boundsSwLng)) || 0;
        const maxLng = Math.max(...this.geofences.map((x) => x.boundsNeLng)) || 0;
        const marginV = getContainerWidth('selector-map-container') / 5;
        const marginH = getContainerHeight('selector-map-container') / 5;
        this.map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          {
            padding: {
              left: marginH,
              right: marginH,
              top: marginV,
              bottom: marginV,
            },
            duration: 0,
          },
        );
      },
      zoomToGeofence(g) {
        this.map.easeTo({
          center: [g.lng, g.lat],
          zoom: this.map.getZoom() > 15 ? this.map.getZoom() : 15,
        });
      },
      activateGeofence(id, zoomToGeofence = true) {
        [this.activeGeofence] = this.geofences.filter((x) => x.id === id);
        if (zoomToGeofence) {
          this.zoomToGeofence(this.activeGeofence);
        }
      },
    },
  };
</script>
